import { AutomationConditionModel } from '@/typings/api/emailCenter';
import { F } from '@/utils/intl';
import { Tooltip } from 'antd';
import React, { useMemo } from 'react';
import { ConditionOperator, describeConditions } from '../automationConditions';

import styles from './AutomationConditions.less';

/**
 * The operator words, as one literal map.
 *
 * Built from the slugs rather than from `=`/`>=` so every message id is a
 * greppable constant — and so the wording lives next to the other wording
 * instead of inside the pure module that derives the slugs.
 */
const OPERATOR_MESSAGES: Record<
  ConditionOperator,
  { id: string; defaultMessage: string }
> = {
  eq: {
    id: 'pages.emailCenter.condition.operator.eq',
    defaultMessage: 'is',
  },
  neq: {
    id: 'pages.emailCenter.condition.operator.neq',
    defaultMessage: 'is not',
  },
  gt: {
    id: 'pages.emailCenter.condition.operator.gt',
    defaultMessage: 'is more than',
  },
  lt: {
    id: 'pages.emailCenter.condition.operator.lt',
    defaultMessage: 'is less than',
  },
  gte: {
    id: 'pages.emailCenter.condition.operator.gte',
    defaultMessage: 'is at least',
  },
  lte: {
    id: 'pages.emailCenter.condition.operator.lte',
    defaultMessage: 'is at most',
  },
  in: {
    id: 'pages.emailCenter.condition.operator.in',
    defaultMessage: 'is one of',
  },
  not_in: {
    id: 'pages.emailCenter.condition.operator.notIn',
    defaultMessage: 'is none of',
  },
  is_empty: {
    id: 'pages.emailCenter.condition.operator.isEmpty',
    defaultMessage: 'is empty',
  },
  is_not_empty: {
    id: 'pages.emailCenter.condition.operator.isNotEmpty',
    defaultMessage: 'is set',
  },
  has_tag: {
    id: 'pages.emailCenter.condition.operator.hasTag',
    defaultMessage: 'has tag',
  },
  has_not_tag: {
    id: 'pages.emailCenter.condition.operator.hasNotTag',
    defaultMessage: 'does not have tag',
  },
};

interface AutomationConditionsProps {
  conditions: AutomationConditionModel[] | null | undefined;
}

/**
 * An automation step's send conditions as a row of chips.
 *
 * Reads as one sentence — "Only if · city is Berlin · has tag vip" — because
 * that is what the rule is: Mailcoach checks every entry once the step's wait is
 * over and sends only when all of them hold. The "Only if" carries the tooltip
 * with the part the chips cannot show: a contact that fails one does not just
 * skip this mail, it leaves the automation.
 *
 * Renders nothing when the step is unconditional, so the common case adds no
 * line to the row.
 */
const AutomationConditions: React.FC<AutomationConditionsProps> = ({
  conditions,
}) => {
  const readable = useMemo(() => describeConditions(conditions), [conditions]);

  if (readable.length === 0) return null;

  return (
    <div className={styles.conditions}>
      <Tooltip
        title={
          <F
            id="pages.emailCenter.condition.allMustMatch"
            defaultMessage="Checked once the waiting time is over. All of them must match — a contact that fails one leaves the automation and receives none of its later emails."
          />
        }
      >
        <span className={styles.prefix}>
          <F id="pages.emailCenter.condition.onlyIf" defaultMessage="Only if" />
        </span>
      </Tooltip>
      {readable.map((condition, index) => (
        // Conditions have no id of their own and may legitimately repeat, so
        // the evaluation position is the only stable key.
        <span className={styles.chip} key={index}>
          {condition.subject && (
            <span className={styles.subject}>{condition.subject}</span>
          )}
          <span className={styles.operator}>
            <F {...OPERATOR_MESSAGES[condition.operator]} />
          </span>
          {condition.values.length > 0 && (
            <span className={styles.value}>{condition.values.join(', ')}</span>
          )}
        </span>
      ))}
    </div>
  );
};

export default React.memo(AutomationConditions);
